import { UIElement } from "./ui_element.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { TransformOptions } from "./ui_element.js";
import { Vector2 } from "../../common/vector2.js";
export class UIImage extends UIElement
{
  private image: ImageBitmap;
  private img_id: ImgID;

  private corner_radius: number;
  private flip_x = false;
  // Horizontal slice of the source bitmap to draw (fractions 0..1), e.g. the
  // left half of an asymmetric tab image. null draws the whole bitmap.
  private source_x_range: [ number, number ] | null = null;
  private dimmed = false;
  private dimmed_cache: HTMLCanvasElement | null = null;
  private dimmed_cache_source: ImageBitmap | null = null;
  private hue_rotation = 0;
  private hue_cache: HTMLCanvasElement | null = null;
  private hue_cache_source: CanvasImageSource | null = null;

  private static readonly DIM_DESATURATION = 1;
  private static readonly DIM_BRIGHTNESS = 1;
  private render_clip?: { x: number; y: number; width: number; height: number };
  // 1 = fully visible, 0 = fully hidden. Crops the image (source AND dest, so
  // the art isn't stretched) to only its bottom fraction — for liquid/meter-
  // style fills that grow up from the bottom instead of stretching.
  private reveal_from_bottom = 1;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    image_id: ImgID,
    options?: TransformOptions,
    is_scalable_on_click = false,
    corner_radius = 0
  )
  {
    super( ctx, x, y, width, height, false, is_scalable_on_click, options );
    this.img_id = image_id;
    this.image = ImagesHolder.getImage( image_id );
    this.healWhenLoaded();
    this.corner_radius = corner_radius;
  }

  private healWhenLoaded(): void
  {
    if ( !ImagesHolder.isPlaceholder( this.image ) )
      return;
    const id = this.img_id;
    ImagesHolder.whenLoaded( id, bmp =>
    {
      if ( this.img_id === id && ImagesHolder.isPlaceholder( this.image ) )
        this.setImageBitmap( bmp );
    } );
  }
  name():string { return 'UIDrawableImage'; }

  private createRoundedClip( radius: number ): void
  {
    const w = this.rect.getW();
    const h = this.rect.getH();
    const x = -this.rect.getW2();
    const y = -this.rect.getH2();

    this.ctx.beginPath();
    this.ctx.moveTo( x + radius, y );

    this.ctx.lineTo( x + w - radius, y );
    this.ctx.quadraticCurveTo( x + w, y, x + w, y + radius );

    this.ctx.lineTo( x + w, y + h - radius );
    this.ctx.quadraticCurveTo( x + w, y + h, x + w - radius, y + h );

    this.ctx.lineTo( x + radius, y + h );
    this.ctx.quadraticCurveTo( x, y + h, x, y + h - radius );

    this.ctx.lineTo( x, y + radius );
    this.ctx.quadraticCurveTo( x, y, x + radius, y );

    this.ctx.closePath();
    this.ctx.clip();
  }

  public setSourceXRange( from: number, to: number ): void
  {
    const lo = Math.max( 0, Math.min( 1, Math.min( from, to ) ) );
    const hi = Math.max( 0, Math.min( 1, Math.max( from, to ) ) );
    this.source_x_range = lo === 0 && hi === 1 ? null : [ lo, hi ];
    this.setShouldBeRendered();
  }

  public setFlipX( value: boolean ): void
  {
    this.flip_x = value;
    this.setShouldBeRendered();
  }

  public setImageId( image_id: ImgID ): void
  {
    this.img_id = image_id;
    this.image = ImagesHolder.getImage( image_id );
    this.healWhenLoaded();
    this.setShouldBeRendered();
  }

  // For swapping in art with different native dimensions (e.g. a box's
  // closed vs. open cap) without recreating the element — repositions and
  // resizes in one go, since a moved top-left would otherwise no longer
  // line up with the new size's intended anchor. Position is driven by
  // Transformable's pixel_pos (not rect.x/y, which the base class always
  // constructs at local 0,0 and only ever uses for width/height).
  public setBounds( x: number, y: number, width: number, height: number ): void
  {
    this.setPixelPos( new Vector2( x, y ), true );
    this.rect.resize( width, height );
    this.setShouldBeRendered();
  }

  public setImageBitmap( bitmap: ImageBitmap ): void
  {
    this.image = bitmap;
    this.setShouldBeRendered();
  }

  public setRevealFromBottom( fraction: number ): void
  {
    this.reveal_from_bottom = Math.max( 0, Math.min( 1, fraction ) );
    this.setShouldBeRendered();
  }

  public getImage(): ImageBitmap | null { return this.image; }

  public setDimmed( value: boolean ): void
  {
    if ( this.dimmed === value ) return;
    this.dimmed = value;
    this.setShouldBeRendered();
  }

  /**
   * Recolours the bitmap by rotating its hue, keeping shape, gloss and
   * shadows. Lets one button artwork serve several states (the shop's green
   * price button becomes the purple FREE button) until dedicated art exists.
   */
  public setHueRotation( degrees: number ): void
  {
    const normalized = ( ( degrees % 360 ) + 360 ) % 360;
    if ( this.hue_rotation === normalized ) return;
    this.hue_rotation = normalized;
    this.hue_cache = null;
    this.setShouldBeRendered();
  }

  private getHueRotatedImage( source: CanvasImageSource & { width: number; height: number } ): HTMLCanvasElement
  {
    if ( this.hue_cache && this.hue_cache_source === source )
      return this.hue_cache;

    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = source.width;
    off_canvas.height = source.height;
    const off_ctx = off_canvas.getContext( "2d" )!;
    off_ctx.filter = `hue-rotate(${this.hue_rotation}deg)`;
    off_ctx.drawImage( source, 0, 0 );
    off_ctx.filter = "none";
    this.hue_cache = off_canvas;
    this.hue_cache_source = source;
    return off_canvas;
  }

  private getDimmedImage(): HTMLCanvasElement
  {
    if ( this.dimmed_cache && this.dimmed_cache_source === this.image )
      return this.dimmed_cache;

    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = this.image.width;
    off_canvas.height = this.image.height;
    const off_ctx = off_canvas.getContext( "2d" )!;

    off_ctx.drawImage( this.image, 0, 0 );

    const image_data = off_ctx.getImageData( 0, 0, off_canvas.width, off_canvas.height );
    const data = image_data.data;
    const desaturation = UIImage.DIM_DESATURATION;
    const brightness = UIImage.DIM_BRIGHTNESS;

    for ( let i = 0; i < data.length; i += 4 )
    {
      if ( data[i + 3] === 0 ) continue;
      const luminance = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = Math.round( ( data[i] + ( luminance - data[i] ) * desaturation ) * brightness );
      data[i + 1] = Math.round( ( data[i + 1] + ( luminance - data[i + 1] ) * desaturation ) * brightness );
      data[i + 2] = Math.round( ( data[i + 2] + ( luminance - data[i + 2] ) * desaturation ) * brightness );
    }

    off_ctx.putImageData( image_data, 0, 0 );
    this.dimmed_cache = off_canvas;
    this.dimmed_cache_source = this.image;
    return off_canvas;
  }

  /** Clips rendering in image-local coordinates measured from its top-left. */
  public setRenderClip( x: number, y: number, width: number, height: number ): void
  {
    this.render_clip = { x, y, width, height };
    this.setShouldBeRendered();
  }

  public renderRaw(): void
  {
    if ( this.image.width === 0 )
      return;

    this.ctx.drawImage( this.image, -this.rect.getW2(), -this.rect.getH2(), this.rect.getW(), this.rect.getH() );
  }

  render(): void
  {
  if ( !this.shouldBeRendered() )
    return;

  if ( this.image.width <= 1 )
  {
    const fresh = ImagesHolder.getLoadedImage( this.img_id );
    if ( fresh && fresh !== this.image )
    {
      this.image = fresh;
    }
    else if ( this.image.width === 0 )
    {
      void ImagesHolder.ensure( this.img_id ).catch( () => {} );
      return;
    }
  }

  this.startRender();


  const w = this.rect.getW();
  const h = this.rect.getH();
  const w2 = this.rect.getW2();
  const h2 = this.rect.getH2();

  this.applyTransform();

  if ( this.flip_x )
    this.ctx.scale( -1, 1 );

  if ( this.render_clip )
  {
    this.ctx.beginPath();
    this.ctx.rect(
      -w2 + this.render_clip.x,
      -h2 + this.render_clip.y,
      this.render_clip.width,
      this.render_clip.height
    );
    this.ctx.clip();
  }

    if ( this.corner_radius > 0 )
    {
      this.createRoundedClip( this.corner_radius );
    }

  const base_source = this.dimmed
    ? this.getDimmedImage()
    : this.darkness_factor === 0 ? this.image : this.createDarkenedImage( this.image );
  const source = this.hue_rotation !== 0 && !this.dimmed ? this.getHueRotatedImage( base_source ) : base_source;

  if ( this.reveal_from_bottom >= 1 && this.source_x_range )
  {
    const sx = source.width * this.source_x_range[0];
    const sw = Math.max( 1, source.width * ( this.source_x_range[1] - this.source_x_range[0] ) );
    this.ctx.drawImage( source, sx, 0, sw, source.height, -w2, -h2, w, h );
  }
  else if ( this.reveal_from_bottom >= 1 )
  {
    this.ctx.drawImage( source, -w2, -h2, w, h );
  }
  else if ( this.reveal_from_bottom > 0 )
  {
    const sh = source.height * this.reveal_from_bottom;
    const sy = source.height - sh;
    const dh = h * this.reveal_from_bottom;
    const dy = h2 - dh;
    this.ctx.drawImage( source, 0, sy, source.width, sh, -w2, dy, w, dh );
  }

  this.finishRender();
}

  public getRectX(): number { return this.rect.getX(); }
  public getRectY(): number { return this.rect.getY(); }
  public getRectW(): number { return this.rect.getW(); }
  public getRectH(): number { return this.rect.getH(); }
}
